import React, { createContext, useContext, useEffect, useState } from 'react';

export enum UITheme {
  LIGHT = 'light',
  DARK = 'dark',
}

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: UITheme;
  storageKey?: string;
};

type ThemeProviderState = {
  uiTheme: UITheme;
  setTheme: (theme: UITheme) => void;
};

const initialState: ThemeProviderState = {
  uiTheme: UITheme.LIGHT,
  setTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
  children,
  defaultTheme = UITheme.LIGHT,
  storageKey = 'ingenia-ui-theme',
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<UITheme>(
    (localStorage.getItem(storageKey) as UITheme) || defaultTheme,
  );

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove(UITheme.LIGHT, UITheme.DARK);
    localStorage.setItem(storageKey, theme);
    root.classList.add(theme);
  }, [storageKey, theme]);

  return (
    <ThemeProviderContext.Provider
      {...props}
      value={{
        uiTheme: theme,
        setTheme,
      }}
    >
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined)
    throw new Error('useTheme must be used within a ThemeProvider');

  return context;
};
